import { TypedDocumentNode, gql } from '@apollo/client';
import { MessageDescriptor } from 'react-intl';

import { Rarity, So5LeaderboardType, Sport } from '__generated__/globalTypes';
import { FRONTEND_ASSET_HOST } from 'constants/assets';
import { TotalAveragePlayerScore } from 'lib/averagePlayerScore';
import { dailySwingTournamentTypes } from 'lib/baseball';
import { cardAttributes } from 'lib/glossary';
import { isType, isTypeFn, withFragments } from 'lib/gql';
import { FragmentOf, unmaskFragment } from 'lib/graphql';
import { dailyHoopsTournamentTypes } from 'lib/nba';

import type {
  isSpecialEventSo5LeaderboardType_so5Leaderboard,
  leaderboardHasEntryFees_so5Leaderboard,
  validateVaultRequirements_so5Leaderboard,
} from './__generated__/so5.graphql';
import {
  formatLineupDisplayName_so5Lineup,
  nationalTeamPictureUrl_nationalTeam,
  smallTeamPictureUrl_teamInterface,
  teamPictureUrl_teamInterface,
} from './__generated__/so5.graphql';

export const OPTIMISTIC_SLOTS_COUNT_PER_SPORT: Record<Sport, number> = {
  [Sport.FOOTBALL]: 5,
  [Sport.NBA]: 5,
  [Sport.BASEBALL]: 7,
};

export const formatLineupDisplayName = withFragments(
  (so5Lineup_: FragmentOf<formatLineupDisplayName_so5Lineup>) => {
    const so5Lineup = unmaskFragment(so5Lineup_);
    const { name, so5Leaderboard } = so5Lineup;
    const { displayName, trainingCenter } = so5Leaderboard || {};

    if (
      so5Leaderboard?.so5TournamentType.so5LeaderboardType ===
      So5LeaderboardType.SPECIAL_WEEKLY
    ) {
      return so5Leaderboard.so5LeaderboardGroup.displayName;
    }

    return [displayName, trainingCenter && name].filter(Boolean).join(' ');
  },
  {
    so5Lineup: gql`
      fragment formatLineupDisplayName_so5Lineup on So5Lineup {
        id
        name
        so5Leaderboard {
          slug
          displayName
          trainingCenter
          so5TournamentType {
            id
            so5LeaderboardType
          }
          so5LeaderboardGroup {
            slug
            displayName
          }
        }
      }
    ` as TypedDocumentNode<formatLineupDisplayName_so5Lineup>,
  }
);

export enum PlayerScoreStatus {
  NO_GAME = 'no_game',
  DID_NOT_PLAY = 'did_not_play',
  REVIEWING = 'reviewing',
  PENDING = 'pending',
}

export enum FixtureState {
  opened = 'opened',
  started = 'started',
  closed = 'closed',
}

export const isRarityStr = (rarity: Nullable<string>): rarity is Rarity =>
  !rarity || rarity in Rarity;

export const modeTooltipMessages: Record<
  TotalAveragePlayerScore,
  MessageDescriptor
> = {
  LAST_FIVE_SO5_AVERAGE_SCORE: cardAttributes.L5Score,
  LAST_FIFTEEN_SO5_AVERAGE_SCORE: cardAttributes.L15Score,
  LAST_TEN_PLAYED_SO5_AVERAGE_SCORE: cardAttributes.L10Score,
  SEASON_AVERAGE_SCORE: cardAttributes.seasonAverageScore,
  LAST_FORTY_SO5_AVERAGE_SCORE: cardAttributes.L40Score,
};

const nationalTeamPictureUrl = withFragments(
  (nationalTeam_: FragmentOf<nationalTeamPictureUrl_nationalTeam>) => {
    const nationalTeam = unmaskFragment(nationalTeam_);
    return `${FRONTEND_ASSET_HOST}/flags/${nationalTeam.country.code}.svg`;
  },
  {
    nationalTeam: gql`
      fragment nationalTeamPictureUrl_nationalTeam on NationalTeam {
        slug
        country {
          slug
          code
        }
      }
    ` as TypedDocumentNode<nationalTeamPictureUrl_nationalTeam>,
  }
);

export const teamPictureUrl = withFragments(
  (team: teamPictureUrl_teamInterface) => {
    if (isType(team, 'Club')) {
      return team.pictureUrl;
    }

    return nationalTeamPictureUrl(team);
  },
  {
    teamInterface: gql`
      fragment teamPictureUrl_teamInterface on TeamInterface {
        slug
        pictureUrl
        ... on NationalTeam {
          slug
          ...nationalTeamPictureUrl_nationalTeam
        }
      }
      ${nationalTeamPictureUrl.fragments.nationalTeam}
    ` as TypedDocumentNode<teamPictureUrl_teamInterface>,
  }
);

export const smallTeamPictureUrl = withFragments(
  (team: smallTeamPictureUrl_teamInterface) => {
    if (isType(team, 'Club')) {
      return team.avatarPictureUrl;
    }

    return nationalTeamPictureUrl(team);
  },
  {
    teamInterface: gql`
      fragment smallTeamPictureUrl_teamInterface on TeamInterface {
        slug
        avatarPictureUrl: pictureUrl(derivative: "avatar")
        ... on NationalTeam {
          slug
          ...nationalTeamPictureUrl_nationalTeam
        }
      }
      ${nationalTeamPictureUrl.fragments.nationalTeam}
    ` as TypedDocumentNode<smallTeamPictureUrl_teamInterface>,
  }
);

export const isSpecialEventSo5LeaderboardType = withFragments(
  (so5Leaderboard?: isSpecialEventSo5LeaderboardType_so5Leaderboard) => {
    return (
      so5Leaderboard &&
      (dailySwingTournamentTypes.includes(
        so5Leaderboard.so5TournamentType.so5LeaderboardType
      ) ||
        dailyHoopsTournamentTypes.includes(
          so5Leaderboard.so5TournamentType.so5LeaderboardType
        ))
    );
  },
  {
    so5Leaderboard: gql`
      fragment isSpecialEventSo5LeaderboardType_so5Leaderboard on So5Leaderboard {
        slug
        so5TournamentType {
          id
          so5LeaderboardType
        }
      }
    ` as TypedDocumentNode<isSpecialEventSo5LeaderboardType_so5Leaderboard>,
  }
);

export const validateVaultRequirements = withFragments(
  (so5Leaderboard?: validateVaultRequirements_so5Leaderboard) => {
    if (!so5Leaderboard) {
      return {
        minVaultBoost: undefined,
        isMissingVaultPoints: false,
        missingVaultPointsCount: 0,
      };
    }
    const minVaultBoost = so5Leaderboard.displayedTypedRules.find(
      isTypeFn('VaultPointsRule')
    )?.minVaultBoost;
    const missingVaultPoints = so5Leaderboard.canCompose?.missingVaultPoints;
    return {
      minVaultBoost,
      isMissingVaultPoints: !!missingVaultPoints,
      missingVaultPointsCount: missingVaultPoints,
    };
  },
  {
    so5Leaderboard: gql`
      fragment validateVaultRequirements_so5Leaderboard on So5Leaderboard {
        slug
        canCompose {
          missingVaultPoints
        }
        displayedTypedRules {
          key
          ... on VaultPointsRule {
            rarity
            minVaultBoost {
              points
              boostLevel {
                milestone
              }
            }
          }
        }
      }
    ` as TypedDocumentNode<validateVaultRequirements_so5Leaderboard>,
  }
);

export const leaderboardHasEntryFees = withFragments(
  (so5Leaderboard?: leaderboardHasEntryFees_so5Leaderboard) =>
    so5Leaderboard?.displayedTypedRules?.some(
      rule => rule.__typename === 'So5EntryItemRule'
    ),
  {
    so5Leaderboard: gql`
      fragment leaderboardHasEntryFees_so5Leaderboard on So5Leaderboard {
        slug
        displayedTypedRules {
          key
          ... on So5EntryItemRule {
            key
          }
        }
      }
    ` as TypedDocumentNode<leaderboardHasEntryFees_so5Leaderboard>,
  }
);
